import { z } from 'zod';
import { qaCategorySchema } from './generation.js';
import { qaScorecardSchema } from './qa-score.js';
const count = z.number().int().nonnegative();
/** How one role answered across a lesson's build — GEN-186. Null where it took no turn. */
export const ledgerRoleSchema = z.strictObject({
    model: z
        .string()
        .nullable()
        .describe('The model that answered its last turn, a fallback included.'),
    effort: z.string().nullable(),
    path: z.enum(['claude-code', 'api']).nullable(),
    turns: count,
});
/** One role's turns on one row, wait included — what `GenerationRow.spent` holds. */
const roleSpendSchema = ledgerRoleSchema.extend({ waitedMs: count });
export const rowSpendSchema = z.strictObject({
    researcher: roleSpendSchema.optional(),
    writer: roleSpendSchema.optional(),
    reviewer: roleSpendSchema.optional(),
    editor: roleSpendSchema.optional(),
});
/** What cost a category its points, by finding source — the sources GEN-185 names. */
export const ledgerCostsSchema = z.strictObject({
    storyBudget: count,
    readability: count,
    coachVoice: count,
    visuals: count,
    cast: count,
    model: count,
});
/** One repair round, counted for one lesson — the numbers GEN-182's round line logs. */
export const ledgerRoundSchema = z.strictObject({
    round: z.number().int().min(1),
    fixed: count,
    unresolved: count,
    unsent: count,
    failed: count,
    disputed: count,
    new: count,
});
/** Named one at a time rather than `z.record`, for `qaScorecardSchema.scores`' reason. */
const ledgerCostsByCategorySchema = z.strictObject({
    GROUNDING: ledgerCostsSchema,
    COMPREHENSION: ledgerCostsSchema,
    INSTRUCTIONAL_DESIGN: ledgerCostsSchema,
    MECHANICS: ledgerCostsSchema,
    WRITING: ledgerCostsSchema,
    RELEVANCE: ledgerCostsSchema,
});
/** One lesson build, written by the build when its last review settles — GEN-186. */
export const generationLedgerRowSchema = z.strictObject({
    id: z.string(),
    createdAt: z.iso.datetime(),
    lessonId: z
        .string()
        .nullable()
        .describe('The lesson a person opens; null once it is deleted.'),
    lessonSlug: z.string(),
    lessonTitle: z.string(),
    jobId: z.string(),
    rowId: z.string().describe('The REVIEW row that settled it.'),
    release: z.string().describe('The git sha the API ran.'),
    research: z
        .boolean()
        .describe('Whether its job researched each module first — GEN-218.'),
    researcher: ledgerRoleSchema.describe("Its module's research turns, shared by the module's lessons. No turns when research was off or the module already had a pack."),
    writer: ledgerRoleSchema.describe("The lesson row's writing turns."),
    reviewer: ledgerRoleSchema,
    editor: ledgerRoleSchema,
    lessonsReviewed: z
        .number()
        .int()
        .min(1)
        .describe("Lessons the review row judged together. The reviewer's and editor's turns are that row's, shared by them."),
    qa: qaScorecardSchema.pick({
        overall: true,
        status: true,
        blockers: true,
        scores: true,
    }),
    costs: ledgerCostsByCategorySchema,
    repairs: z.array(ledgerRoundSchema),
    minutes: z
        .number()
        .nonnegative()
        .describe("From the module's plan row starting to this review settling."),
    waitedMs: count.describe('How long its turns queued for a Claude Code slot, every role.'),
});
export const generationLedgerQuerySchema = z.object({
    model: z
        .string()
        .trim()
        .min(1)
        .optional()
        .describe('Rows any of whose roles ran on this model.'),
    research: z
        .enum(['on', 'off'])
        .optional()
        .describe('Rows whose job researched first, or did not — GEN-218.'),
    from: z.iso.date().optional().describe('The first day included, UTC.'),
    to: z.iso.date().optional().describe('The last day included, UTC.'),
    limit: z.coerce.number().int().min(1).max(100).default(25),
    offset: z.coerce.number().int().min(0).default(0),
});
export const generationLedgerPageSchema = z.strictObject({
    rows: z.array(generationLedgerRowSchema).describe('Newest first.'),
    total: count,
    models: z
        .array(z.string())
        .describe('Every model the ledger names, for the filter.'),
});
const ledgerCostSourceSchema = ledgerCostsSchema.keyof();
export const generationPatternsQuerySchema = z.object({
    days: z.coerce
        .number()
        .int()
        .min(1)
        .max(90)
        .default(14)
        .describe('How many days back the builds are read from.'),
});
/** One recurring problem, as a sentence, and what the rule table proposes — GEN-187. */
const ledgerPatternSchema = z.strictObject({
    kind: z.enum(['category', 'repairs', 'models']),
    text: z.string(),
    proposal: z
        .string()
        .nullable()
        .describe('Null where no rule in the table reached its threshold.'),
});
/** Computed on read from the ledger, so nothing is stored or scheduled — GEN-187. */
export const generationPatternsSchema = z.strictObject({
    days: z.number().int().min(1),
    builds: count,
    categories: z.array(z.strictObject({
        category: qaCategorySchema,
        below: count.describe('Builds that scored it under the review line.'),
        costliest: ledgerCostSourceSchema
            .nullable()
            .describe('What cost it most in the most of those builds.'),
        costliestBuilds: count,
    })),
    repairs: z.strictObject({
        rounds: count,
        newPerRound: z.number().nonnegative(),
        unsent: count,
        failed: count,
    }),
    models: z
        .array(z.strictObject({
        writer: z.string().nullable(),
        reviewer: z.string().nullable(),
        builds: count,
        medianQa: z.number(),
    }))
        .describe('Writer and reviewer pairs, best median first.'),
    patterns: z.array(ledgerPatternSchema),
});
//# sourceMappingURL=generation-ledger.js.map